import React from "react";
import { Clock, ExternalLink, LogOut, QrCode, Umbrella } from "lucide-react";
import { useIonRouter } from "@/app/IonicContextProvider";
import { useLocation } from "react-router-dom";
import { cn } from "@/shared/utils/cn";
import { usePatrolConfig } from "@/features/patrol/hooks";
import { useReportPermissions } from "@/features/reports/hooks";
import { useAuth } from "@/features/auth/hooks";
import { config } from "@/lib/config";

type NavigationItem = {
  name: string;
  icon: typeof Clock;
  href?: string;
  onClick?: () => void;
  testId?: string;
};

const baseNavigation: NavigationItem[] = [
  { name: "Shifts", href: "/shifts", icon: Clock, testId: "nav-shifts" },
  { name: "Leave", href: "/leave", icon: Umbrella, testId: "nav-leave" },
];

export function Sidebar({ isOpen = true, onClose }: { isOpen?: boolean; onClose?: () => void }) {
  const router = useIonRouter();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { data: patrolConfig } = usePatrolConfig();
  const { data: reportPermissions } = useReportPermissions();
  const path = location?.pathname ?? "/";

  const managerAppUrl = config.app.managerAppUrl;
  const canViewReports =
    !!reportPermissions &&
    (reportPermissions.view_shift_reports || reportPermissions.view_patrol_reports) &&
    !!managerAppUrl;

  const navigation: NavigationItem[] = [
    ...baseNavigation,
    ...(patrolConfig !== null && patrolConfig !== undefined
      ? [{ name: "Patrol", href: "/patrol/scan", icon: QrCode, testId: "nav-patrol" }]
      : []),
    ...(canViewReports
      ? [
          {
            name: "View reports",
            icon: ExternalLink,
            onClick: () => {
              if (managerAppUrl) {
                window.open(managerAppUrl, "_blank", "noopener,noreferrer");
              }
            },
            testId: "nav-view-reports",
          } as NavigationItem,
        ]
      : []),
  ];


  const handleNavigation = (item: NavigationItem) => {
    if (item.onClick) {
      item.onClick();
      onClose?.();
      return;
    }
    const href = item.href;
    if (!href) return;
    router.push(href, "root", "replace"); // smoother, avoids stacking
    onClose?.();
  };

  const displayName = user?.name || user?.whatsapp_number;

  const handleLogout = () => {
    void logout();
    onClose?.();
  };

  return (
    <aside
      className={cn(
        // ✅ Mobile/Tablet: fixed drawer under the header; Desktop: fixed sidebar always visible
        "w-64 bg-card border-r border-border transition-transform left-0 safe-area-bottom",
        "fixed top-16 bottom-0 h-[calc(100vh-4rem)] z-50 flex flex-col", // 4rem ~= header height; z-50 above overlay
        // On mobile/tablet: show/hide based on isOpen. On desktop (lg+): always visible via lg:translate-x-0
        isOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0 lg:z-40" // Always visible on desktop, lower z-index than mobile overlay
      )}
      data-testid="sidebar"
    >
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
        {navigation.map((item) => {
          const isActive =
            !!item.href &&
            (path === item.href ||
              (item.href === "/tasks" && path.startsWith("/tasks")) ||
              (item.href === "/shifts" && path.startsWith("/shifts")) ||
              (item.href === "/patrol/scan" && path.startsWith("/patrol")));

          return (
            <button
              key={item.name}
              onClick={() => handleNavigation(item)}
              className={cn(
                "w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors",
                isActive
                  ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors tap-highlight-transparent"
                  : "text-muted-foreground hover:text-white hover:bg-accent"
              )}
              data-testid={item.testId ?? `nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon className="h-4 w-4" />
              <span className="font-medium">{item.name}</span>
            </button>
          );
        })}
      </nav>

      {user ? (
        <footer
          className="mt-auto shrink-0 border-t border-border bg-muted/30 px-4 py-4"
          data-testid="sidebar-footer"
        >
          <p className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-300">
            {displayName}
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm font-medium text-red-700 transition-colors hover:text-red-800 dark:text-red-500 dark:hover:text-red-400 tap-highlight-transparent"
            data-testid="sidebar-logout"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            Logout
          </button>
        </footer>
      ) : null}
    </aside>
  );
}
